import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { getCollectedSync } from "../api/gallery/collectedSync";
import { getGalleryProgress, setGalleryCompletedGrade, startGallerySolves, } from "../api/gallery/galleryProgress";
import { getGallerySets, } from "../api/gallery/getGallerySets";
import { GALLERY_POLL_MS } from "../gallery/grades";
import { galleryGradesBySetId, galleryOrder, } from "../gallery/list";
import { isUnauthorized, pollDelay } from "../gallery/poll";
import { useCollectedSync, useGalleryPlanLimits, } from "./useGalleryAccountData";
// The slowest the shown solve count ticks up by one.
const COUNT_STEP_MS = 80;
// How long a finished run's full count stays up before Solutions built.
const FULL_COUNT_HOLD_MS = 900;
// How often a followed collected-players sync is read.
const COLLECTED_SYNC_POLL_MS = 2000;
/**
 * The Gallery list's data on both platforms: the sets, the user's progress,
 * the backend's solve run (polled while it runs) and completing grades.
 * Filters, sort controls and layout stay with each app.
 */
export const useGalleryList = ({ userUuid, platform, followCollectedSync = false, }) => {
    const [categories, setCategories] = useState([]);
    const [status, setStatus] = useState("pending");
    const [progress, setProgress] = useState(new Map());
    // Bumped to refetch progress (a club import, a sync, a First Owner change).
    const [progressVersion, setProgressVersion] = useState(0);
    // The user whose progress has landed (or failed); until then their cards show placeholders.
    const [progressLoadedFor, setProgressLoadedFor] = useState();
    // A progress read is in flight, for pull-to-refresh spinners.
    const [fetching, setFetching] = useState(false);
    // The backend's solve run, polled while it runs.
    const [run, setRun] = useState();
    // Several solves land between two polls, so the shown count ticks up one at a time.
    const [shownDone, setShownDone] = useState(0);
    const [newCollectedCount, setNewCollectedCount] = useState(0);
    const limits = useGalleryPlanLimits(userUuid);
    // The "x / y players collected" counts.
    const { collectedSync, setCollectedSync } = useCollectedSync(userUuid, progressVersion);
    // Bumped on a fresh load and each poll of a run: the hosts re-sort on it.
    const [resortVersion, setResortVersion] = useState(0);
    // The user the order last took progress from, so only a fresh load re-sorts.
    const sortedForUser = useRef(undefined);
    // Between the Build click and the backend's answer.
    const [starting, setStarting] = useState(false);
    const refresh = useCallback(() => setProgressVersion((v) => v + 1), []);
    useEffect(() => {
        let cancelled = false;
        getGallerySets()
            .then((result) => {
            if (cancelled)
                return;
            setCategories(result);
            setStatus("fulfilled");
        })
            .catch(() => {
            if (!cancelled)
                setStatus("rejected");
        });
        return () => {
            cancelled = true;
        };
    }, []);
    // Read through a ref, so handlers stay stable and memoized cards don't all re-render.
    const progressRef = useRef(progress);
    progressRef.current = progress;
    /**
     * Unchanged sets keep their object, so only the cards that changed
     * re-render, and an unchanged poll keeps the whole Map. Returns whether
     * anything changed, so a poll only re-sorts when it has news.
     */
    // Per set: the grade the backend last confirmed and the latest save, so
    // only the newest failed save rolls back, and to what is really stored.
    const savedGrades = useRef(new Map());
    const gradeSeqs = useRef(new Map());
    const applyProgress = useCallback((result) => {
        // Fresh from the backend: the confirmed grades come from it again.
        savedGrades.current.clear();
        const prev = progressRef.current;
        let changed = prev.size !== result.sets.length;
        const next = new Map(result.sets.map((p) => {
            const old = prev.get(p.setId);
            const same = !!old && JSON.stringify(old) === JSON.stringify(p);
            if (!same)
                changed = true;
            return [p.setId, same ? old : p];
        }));
        if (changed)
            setProgress(next);
        setRun(result.run);
        setNewCollectedCount(result.newCollectedCount);
        return changed;
    }, []);
    useEffect(() => {
        // A refetch keeps the current progress on screen until the new one lands.
        if (sortedForUser.current !== userUuid) {
            // Through the ref too, so the next answer compares against nothing.
            progressRef.current = new Map();
            setProgress(progressRef.current);
        }
        if (!userUuid)
            return;
        let cancelled = false;
        setFetching(true);
        getGalleryProgress()
            .then((result) => {
            if (cancelled)
                return;
            applyProgress(result);
            // Only solves landing while polling count up; a finished run's count shows as is.
            setShownDone(result.run.done);
            setProgressLoadedFor(userUuid);
            if (sortedForUser.current !== userUuid) {
                sortedForUser.current = userUuid;
                setResortVersion((v) => v + 1);
            }
        })
            // Failed: the cards fall back to the market solutions instead of waiting forever.
            .catch(() => {
            if (!cancelled)
                setProgressLoadedFor(userUuid);
        })
            .finally(() => {
            if (!cancelled)
                setFetching(false);
        });
        return () => {
            cancelled = true;
            setFetching(false);
        };
    }, [userUuid, progressVersion, applyProgress]);
    // Each set swaps in as it lands.
    useEffect(() => {
        if (!userUuid || !run?.running)
            return;
        let cancelled = false;
        let timer;
        let failures = 0;
        const poll = () => {
            const delay = pollDelay(GALLERY_POLL_MS, failures);
            timer = setTimeout(() => {
                getGalleryProgress()
                    .then((result) => {
                    if (cancelled)
                        return;
                    failures = 0;
                    // Sets move into place as their solutions land; a quiet poll moves nothing.
                    if (applyProgress(result))
                        setResortVersion((v) => v + 1);
                    if (result.run.running)
                        poll();
                })
                    .catch((error) => {
                    if (cancelled || isUnauthorized(error))
                        return;
                    failures += 1;
                    poll();
                });
            }, delay);
        };
        poll();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [userUuid, run?.running, applyProgress]);
    const runDone = run?.done ?? 0;
    // Clamped, so a new run's first frame doesn't show the last run's full count.
    const shown = Math.min(shownDone, runDone);
    useEffect(() => {
        // A new run starts from 0.
        if (shownDone > runDone)
            return setShownDone(runDone);
        if (shownDone === runDone)
            return;
        // Fast enough to catch up before the next poll lands.
        const timer = setTimeout(() => setShownDone((d) => d + 1), Math.min(COUNT_STEP_MS, GALLERY_POLL_MS / (runDone - shownDone)));
        return () => clearTimeout(timer);
    }, [shownDone, runDone]);
    // Still counting up after the backend finished.
    const counting = !!run?.running || shownDone < runDone;
    // A run counted live holds its full count a beat; a finished run found on load doesn't.
    // Set during render, not in an effect, so no frame falls between the count and the hold.
    const [holdingFull, setHoldingFull] = useState(false);
    const [wasCounting, setWasCounting] = useState(counting);
    if (counting !== wasCounting) {
        setWasCounting(counting);
        if (!counting)
            setHoldingFull(true);
    }
    useEffect(() => {
        if (!holdingFull)
            return;
        const timer = setTimeout(() => setHoldingFull(false), FULL_COUNT_HOLD_MS);
        return () => clearTimeout(timer);
    }, [holdingFull]);
    const syncRunning = followCollectedSync && collectedSync?.status === "running";
    useEffect(() => {
        if (!syncRunning)
            return;
        let cancelled = false;
        let timer;
        let failures = 0;
        const poll = () => {
            const delay = pollDelay(COLLECTED_SYNC_POLL_MS, failures);
            timer = setTimeout(() => {
                getCollectedSync()
                    .then((result) => {
                    if (cancelled)
                        return;
                    failures = 0;
                    setCollectedSync(result);
                    // Done or paused: the counts and the solutions' freshness changed.
                    if (result.status === "running")
                        poll();
                    else
                        setProgressVersion((v) => v + 1);
                })
                    .catch((error) => {
                    if (cancelled || isUnauthorized(error))
                        return;
                    failures += 1;
                    poll();
                });
            }, delay);
        };
        poll();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [syncRunning]);
    const setCompletedGrade = useCallback((setId, completedGrade) => {
        if (!savedGrades.current.has(setId))
            savedGrades.current.set(setId, progressRef.current.get(setId)?.completedGrade ?? null);
        const seq = (gradeSeqs.current.get(setId) ?? 0) + 1;
        gradeSeqs.current.set(setId, seq);
        const apply = (grade) => setProgress((prev) => {
            const p = prev.get(setId);
            return p
                ? new Map(prev).set(setId, { ...p, completedGrade: grade })
                : prev;
        });
        apply(completedGrade);
        // Resolves to whether it saved, for a caller that wants to say so.
        return setGalleryCompletedGrade(setId, completedGrade).then(() => {
            savedGrades.current.set(setId, completedGrade);
            return true;
        }, () => {
            if (gradeSeqs.current.get(setId) === seq)
                apply(savedGrades.current.get(setId) ?? null);
            return false;
        });
    }, []);
    /** Queues the solves in the backend; the poll reports them. The host gates login and Credits first. */
    const startSolves = useCallback(async (scope) => {
        setStarting(true);
        try {
            setRun(await startGallerySolves(scope));
        }
        catch {
            // The Build button stays open to try again.
        }
        finally {
            setStarting(false);
        }
    }, []);
    const gradesBySetId = useMemo(() => galleryGradesBySetId(categories, progress, platform), [categories, progress, platform]);
    return {
        categories,
        status,
        progress,
        progressLoading: !!userUuid && progressLoadedFor !== userUuid,
        refresh,
        fetching,
        gradesBySetId,
        run,
        shownDone: shown,
        /** A run is counting up, or holding its full count. */
        runShown: counting || holdingFull,
        starting,
        newCollectedCount,
        limits,
        collectedSync,
        resortVersion,
        setCompletedGrade,
        startSolves,
        /** At least one set has a solution: the first build happened. */
        builtAnySolution: [...progress.values()].some((p) => p.solution),
    };
};
/**
 * The order, each set's grade and the completed sets, frozen until one of
 * `resortOn` changes: marking a set or picking a grade keeps everything in place. A
 * layout effect, so the first order lands in the same paint as the cards.
 */
export const useGalleryOrder = ({ categories, gradesBySetId, progress, sort, plannedGrades, coins, resortOn, }) => {
    const [order, setOrder] = useState(() => ({
        orderedIds: [],
        sortPicks: new Map(),
        completedIds: new Set(),
    }));
    useLayoutEffect(() => {
        setOrder(galleryOrder({
            categories,
            gradesBySetId,
            progress,
            sort,
            plannedGrades,
            coins,
        }));
        // Progress and grades left out on purpose: they must not re-sort.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, sort, coins, ...resortOn]);
    return order;
};
//# sourceMappingURL=useGalleryList.js.map